import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAnimatedToggle } from "@/hooks/useAnimatedToggle";
import { sfx } from "@/lib/sfx";

interface P { open: boolean; onClose: () => void; children: ReactNode; title?: string; width?: number }

/**
 * Portal-rendered modal — reparented to <body>, so it's never trapped by an
 * ancestor's overflow:hidden, backdrop-filter, transform, or contain: paint
 * (any of which promote a fixed-positioned descendant into their containing
 * block on Chromium). Without the portal, mounting <SettingsModal/> from
 * inside <TopBar/> caused the modal to render inside the .topbar's 60px band.
 */
export function Modal({ open, onClose, children, title, width = 520 }: P) {
  const { mounted, visible } = useAnimatedToggle(open);
  const lastOpen = useRef(open);
  useEffect(() => {
    if (open && !lastOpen.current) sfx.swoosh();
    if (!open && lastOpen.current) sfx.reverseSwoosh();
    lastOpen.current = open;
  }, [open]);
  useEffect(() => {
    const on = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [open, onClose]);
  if (!mounted) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: `rgba(0,0,0,${visible ? .55 : 0})`,
        display: "grid", placeItems: "center",
        transition: "background .2s ease",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elev)",
          borderRadius: "var(--radius)",
          padding: 22,
          width: `min(${width}px, 92vw)`,
          maxHeight: "calc(100dvh - 32px)",
          overflowY: "auto",
          boxShadow: "var(--shadow)",
          transform: visible ? "translateY(0)" : "translateY(12px)",
          opacity: visible ? 1 : 0,
          transition: "transform .22s ease, opacity .22s ease",
        }}
      >
        {title && <h2 style={{ margin: "0 0 12px" }}>{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
